import { normalizeName } from '@obraplan/core/domain/shared/naming';
import type {
  CatalogItem,
  CatalogService,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';

/**
 * O código de compras de um item que já existe (Fase 29, RN-395 e RN-396).
 *
 * Mora fora do redutor e fora da tela porque os dois fazem a mesma pergunta: a tela lista os
 * itens que receberiam o código, e o redutor confere a lista de novo antes de gravar. Escrita
 * duas vezes, a regra divergiria na primeira mudança — e a pessoa aceitaria uma lista para
 * ver outra aplicada.
 *
 * ## O item continua o mesmo
 *
 * A chave do item é a descrição normalizada (RN-251), e não o código: é por ela que a
 * quantidade de cada tipologia, o motivo, a peça importada e a divergência do apto o acham
 * (`itemQuantityKey`). Trocar o código troca só a identidade do **material** na
 * consolidação, que une pelo código quando ele existe (RN-011) — e por isso nada precisa
 * ser rechaveado.
 */

/** Um item da obra, com o lugar onde ele mora — o que a pergunta de propagar lista. */
export interface ItemNaObra {
  readonly serviceKey: string;
  readonly itemKey: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly description: string;
}

/**
 * O mesmo código, como a consolidação e o cadastro de materiais o comparam: `e-685110` e
 * `E-685110` são o mesmo tijolo (RN-011).
 */
export function mesmoCodigo(a: string, b: string): boolean {
  return normalizeName(a) === normalizeName(b);
}

/**
 * O outro item do serviço que já carrega este código, quando há.
 *
 * Dois itens do mesmo serviço com o mesmo código são o mesmo material contado duas vezes
 * (RN-251) — a mesma recusa do "Novo item". O próprio item não conta: escolher de novo o
 * código que ele já tem não é repetir nada.
 */
export function outroItemComCodigo(
  servico: Pick<CatalogService, 'items'>,
  itemKey: string,
  code: string,
): CatalogItem | undefined {
  return servico.items.find(
    (item) => item.key !== itemKey && item.code !== undefined && mesmoCodigo(item.code, code),
  );
}

/**
 * Os itens que recebem o código junto (RN-396): a **mesma descrição**, **sem código**, em
 * outro serviço da obra.
 *
 * "Mesma descrição" é a regra da consolidação para o item sem código (`normalizeName`: sem
 * diferenciar maiúsculas nem espaço sobrando). É de propósito a mesma: hoje esses itens já
 * são **um** material na consolidação, e dar código a um só deles o separaria dos outros —
 * a tábua viraria duas linhas de compra onde havia uma. Grafia com acento diferente já é
 * outro material lá, e continua sendo aqui.
 *
 * Ficam de fora, sempre:
 *
 * - quem **já tem código**, igual ou outro — o código de alguém é decisão de alguém, e esta
 *   pergunta não a desfaz;
 * - a **mão de obra**, que não tem código (RN-252);
 * - o item cujo serviço **já tem outro item com este código** — recebê-lo seria repetir o
 *   material dentro do serviço (RN-251).
 *
 * O serviço do próprio item não entra: dentro dele a descrição é a chave, e não se repete.
 */
export function itensParaPropagarCodigo(
  catalog: Pick<ServiceCatalog, 'disciplines'>,
  serviceKey: string,
  itemKey: string,
  code: string,
): readonly ItemNaObra[] {
  const origem = catalog.disciplines
    .flatMap((disciplina) => disciplina.services)
    .find((servico) => servico.key === serviceKey)
    ?.items.find((item) => item.key === itemKey);
  if (origem === undefined || origem.kind === 'MAO_DE_OBRA' || code.trim() === '') return [];
  const descricao = normalizeName(origem.description);

  return catalog.disciplines.flatMap((disciplina) =>
    disciplina.services
      .filter(
        (servico) =>
          servico.key !== serviceKey &&
          !servico.items.some((item) => item.code !== undefined && mesmoCodigo(item.code, code)),
      )
      .flatMap((servico) =>
        servico.items
          .filter(
            (item) =>
              item.kind !== 'MAO_DE_OBRA' &&
              item.code === undefined &&
              normalizeName(item.description) === descricao,
          )
          .map((item): ItemNaObra => ({
            serviceKey: servico.key,
            itemKey: item.key,
            disciplineName: disciplina.name,
            serviceName: servico.name,
            description: item.description,
          })),
      ),
  );
}
